import { ApplicationConfig, provideBrowserGlobalErrorListeners, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withInMemoryScrolling, withPreloading, withViewTransitions } from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { browserPopupRedirectResolver, browserSessionPersistence, getAuth, initializeAuth, provideAuth } from '@angular/fire/auth';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { routes } from './app.routes';
import { SelectivePreloadStrategy } from './core/selective-preload.strategy';
import { authTokenInterceptor } from './core/auth-token.interceptor';
import { environment } from '../environments/environment';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'top',
        anchorScrolling: 'enabled'
      }),
      withPreloading(SelectivePreloadStrategy),
      /**
       * Native View Transitions for route changes. Angular already
       * feature-detects `document.startViewTransition` — browsers
       * without it just navigate normally, no animation, no errors.
       *
       * `onViewTransitionCreated` skips the transition outright for
       * `prefers-reduced-motion`, matching the pattern used elsewhere
       * in this app (see home.component.scss) rather than trying to
       * tone the animation down after the fact.
       */
      withViewTransitions({
        onViewTransitionCreated: ({ transition }) => {
          if (typeof window !== 'undefined' &&
              window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            transition.skipTransition();
          }
        }
      })
    ),
    provideClientHydration(withEventReplay()),
    provideHttpClient(withFetch(), withInterceptors([authTokenInterceptor])),
    provideAnimations(),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => {
      // Server render: no persistence needed.
      if (typeof window === 'undefined') return getAuth();
      // Keep Firebase's ID/refresh tokens OUT of localStorage/IndexedDB.
      // Session persistence lives in sessionStorage (cleared when the tab
      // closes). AuthService switches admin sign-ins to in-memory persistence.
      return initializeAuth(getApp(), {
        persistence: browserSessionPersistence,
        popupRedirectResolver: browserPopupRedirectResolver,
      });
    }),
    // Callable Cloud Functions (rate-limited subscribe / reset / register).
    // Region must match functions/src/index.ts.
    provideFunctions(() => getFunctions(getApp(), 'europe-west2')),
    provideFirestore(() => getFirestore()),
    provideStorage(() => getStorage()),
  ]
};
